import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
} from 'recharts';
import { useMemo } from 'react';
import type { TelemetryRow } from '@/types';
import type { Granularity } from '@/types';
import { formatNumber } from '@/lib/formatters';
import { format } from 'date-fns';
import { useLanguage } from '@/hooks/useLanguage';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export type Metric   = 'voltage' | 'current';
export type PhaseKey = 'R' | 'Y' | 'B' | 'AVG';

export interface PhaseChartProps {
  rows: TelemetryRow[];
  metric: Metric;
  granularity: Granularity;
  visiblePhases: Set<PhaseKey>;
  height?: number;
  /**
   * All rows across every time window for this metric (Today + Week + Month).
   * When supplied, the Y-axis is scaled to the global max so the range stays
   * stable when the user switches between time windows.
   */
  globalRows?: TelemetryRow[];
}

/* -------------------------------------------------------------------------- */
/*  Constants                                                                 */
/* -------------------------------------------------------------------------- */

export const PHASE_META: Record<PhaseKey, { color: string; dash?: string; label: string }> = {
  R:   { color: '#EF4444', label: 'Phase R' },
  Y:   { color: '#F59E0B', label: 'Phase Y' },
  B:   { color: '#0891B2', label: 'Phase B' },
  AVG: { color: 'var(--color-avg-line)', dash: '5 3', label: 'Average' },
};

const MAX_POINTS = 280;

/* -------------------------------------------------------------------------- */
/*  Custom tick components — SVG text with explicit font-weight               */
/* -------------------------------------------------------------------------- */

function XTick({ x, y, payload }: { x?: number; y?: number; payload?: { value: string } }) {
  if (x == null || y == null) return null;
  return (
    <text
      x={x}
      y={y + 10}
      textAnchor="middle"
      fontSize={12}
      fontWeight="700"
      fill="#0F172A"
      fontFamily="inherit"
    >
      {payload?.value}
    </text>
  );
}

function YTick({ x, y, payload }: { x?: number; y?: number; payload?: { value: number } }) {
  if (x == null || y == null) return null;
  return (
    <text
      x={x - 4}
      y={y + 4}
      textAnchor="end"
      fontSize={12}
      fontWeight="700"
      fill="#0F172A"
      fontFamily="inherit"
    >
      {payload?.value != null ? formatNumber(payload.value, 0) : ''}
    </text>
  );
}

/* -------------------------------------------------------------------------- */
/*  LTTB downsampling                                                         */
/* -------------------------------------------------------------------------- */

interface Pt { t: number; R: number; Y: number; B: number; AVG: number }

function lttb(data: Pt[], threshold: number, key: PhaseKey): Pt[] {
  const n = data.length;
  if (n <= threshold) return data;
  const out: Pt[] = [data[0]];
  const bucketSize = (n - 2) / (threshold - 2);
  let a = 0;
  for (let i = 0; i < threshold - 2; i++) {
    const bStart = Math.floor((i + 1) * bucketSize) + 1;
    const bEnd   = Math.min(Math.floor((i + 2) * bucketSize) + 1, n);
    let avgX = 0, avgY = 0;
    const bLen = bEnd - bStart;
    for (let j = bStart; j < bEnd; j++) { avgX += data[j].t; avgY += data[j][key]; }
    avgX /= bLen; avgY /= bLen;
    const rangeStart = Math.floor(i * bucketSize) + 1;
    const rangeEnd   = Math.min(Math.floor((i + 1) * bucketSize) + 1, n);
    const aX = data[a].t, aY = data[a][key];
    let maxArea = -1, maxIdx = rangeStart;
    for (let j = rangeStart; j < rangeEnd; j++) {
      const area = Math.abs((aX - avgX) * (data[j][key] - aY) - (aX - data[j].t) * (avgY - aY)) * 0.5;
      if (area > maxArea) { maxArea = area; maxIdx = j; }
    }
    out.push(data[maxIdx]);
    a = maxIdx;
  }
  out.push(data[n - 1]);
  return out;
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function timeLabel(ts: number, gran: Granularity): string {
  const d = new Date(ts);
  if (gran === 'hourly')  return format(d, 'HH:mm');
  if (gran === 'daily')   return format(d, 'd MMM');
  return format(d, 'MMM yy');
}

/** Full label used in the tooltip — adds HH:mm for daily so the hover shows e.g. "22 May · 14:00" */
function tooltipLabel(ts: number, gran: Granularity): string {
  const d = new Date(ts);
  if (gran === 'hourly')  return format(d, 'HH:mm');
  if (gran === 'daily')   return format(d, 'd MMM · HH:mm');
  return format(d, 'MMM yy');
}

function pctile(sorted: number[], p: number): number {
  if (!sorted.length) return 0;
  const idx = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(idx), hi = Math.ceil(idx);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

/**
 * Dynamic symmetric x-axis tick selector.
 *
 * Picks `targetCount` evenly-spaced labels so ticks are visually symmetric
 * regardless of how many data points exist. Always includes the first and
 * last label so the axis spans edge-to-edge.
 *
 * For 280 down-sampled points this produces 5 clean, evenly-spread labels.
 * For ≤ 5 points every label is shown.
 */
function symmetricXTicks<T extends { label: string }>(data: T[], targetCount = 5): string[] {
  const n = data.length;
  if (n === 0) return [];
  if (n <= targetCount) return data.map(d => d.label);
  const seen = new Set<string>();
  const ticks: string[] = [];
  for (let i = 0; i < targetCount; i++) {
    const idx   = Math.round(i * (n - 1) / (targetCount - 1));
    const label = data[idx].label;
    if (!seen.has(label)) { seen.add(label); ticks.push(label); }
  }
  return ticks;
}

/* -------------------------------------------------------------------------- */
/*  Custom Tooltip                                                            */
/* -------------------------------------------------------------------------- */

function PhaseTooltip({
  active, payload, label, unit, visiblePhases,
}: {
  active?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any[];
  label?: string;
  unit: string;
  visiblePhases: Set<PhaseKey>;
}) {
  const { t } = useLanguage();
  if (!active || !payload?.length) return null;
  const order: PhaseKey[] = ['R', 'Y', 'B', 'AVG'];
  // Use tooltipLabel (date + time for daily) if available, fall back to axis label
  const displayLabel = payload[0]?.payload?.tooltipLabel ?? label;
  return (
    <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2.5 text-xs min-w-[140px]">
      <div className="font-bold text-slate-900 mb-2 text-[12px]">{displayLabel}</div>
      {order.filter((k) => visiblePhases.has(k)).map((k) => {
        const p = payload.find((pl) => pl.dataKey === k);
        if (!p) return null;
        return (
          <div key={k} className="flex items-center justify-between gap-3 mb-1 last:mb-0">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: PHASE_META[k].color }} />
              <span className="font-semibold text-slate-700">{t(PHASE_META[k].label)}</span>
            </span>
            <span className="num font-bold text-slate-900">{formatNumber(p.value, 1)}&thinsp;{unit}</span>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Y-axis nice-scale algorithm                                              */
/* -------------------------------------------------------------------------- */

/**
 * Rounds a raw interval up to the nearest "nice" number.
 * Nice numbers are 1, 2, 2.5, 5, 10 and their powers-of-10 multiples.
 * This guarantees tick labels are always clean, round values.
 */
function niceInterval(rawInterval: number): number {
  if (rawInterval <= 0) return 1;
  const exp   = Math.floor(Math.log10(rawInterval));
  const mag   = Math.pow(10, exp);
  const frac  = rawInterval / mag;               // normalised to [1, 10)
  let niceFrac: number;
  if      (frac <= 1)   niceFrac = 1;
  else if (frac <= 2)   niceFrac = 2;
  else if (frac <= 2.5) niceFrac = 2.5;
  else if (frac <= 5)   niceFrac = 5;
  else                  niceFrac = 10;
  return niceFrac * mag;
}

/**
 * Produces a stable, evenly-spaced Y-axis scale from a set of values.
 *
 * Algorithm:
 *  1. Scan the supplied values for the p1–p99 range (spike-resistant).
 *  2. Apply a metric-specific absolute floor (voltage ≥ 200 V, current ≥ 0 A).
 *  3. Round the interval up to the nearest "nice" step.
 *  4. Align min/max to multiples of that step.
 *  5. Return exactly NUM_TICKS evenly-spaced tick values + the domain.
 *
 * @param vals    All data values to consider (across all visible phases & all windows).
 * @param metric  'voltage' | 'current' — drives the absolute floor.
 * @returns       { domain: [min, max], ticks: number[] }
 */
const NUM_TICKS = 6; // number of Y-axis divisions (produces 6 ticks, 5 intervals)

function niceYScale(
  vals: number[],
  metric: Metric,
): { domain: [number, number]; ticks: number[] } {
  const FALLBACK_VOLT    = { domain: [200, 260] as [number, number], ticks: [200, 210, 220, 230, 240, 250, 260] };
  const FALLBACK_CURR    = { domain: [0,   100] as [number, number], ticks: [0, 20, 40, 60, 80, 100] };
  const fallback = metric === 'voltage' ? FALLBACK_VOLT : FALLBACK_CURR;

  const clean = vals.filter((v) => Number.isFinite(v) && v >= 0);
  if (!clean.length) return fallback;

  const sorted = [...clean].sort((a, b) => a - b);
  const rawLo  = pctile(sorted, 1);
  const rawHi  = pctile(sorted, 99);

  // Absolute floor per metric
  const absFloor = metric === 'voltage' ? 200 : 0;

  // The total span we need to cover, with a small margin so lines don't touch axes
  const span    = Math.max(rawHi - rawLo, 1);
  const margin  = span * 0.08;                   // 8% breathing room on each side

  // Unaligned bounds
  const dataLo = Math.max(absFloor, rawLo - margin);
  const dataHi = rawHi + margin;
  const dataSpan = dataHi - dataLo;

  // Compute the per-interval size for NUM_TICKS - 1 intervals
  const rawStep  = dataSpan / (NUM_TICKS - 1);
  const step     = niceInterval(rawStep);

  // Snap min down and max up to multiples of step
  const niceMin  = Math.floor(dataLo / step) * step;
  const niceMax  = Math.ceil(dataHi  / step) * step;

  // Recalculate step over the snapped range so we get exactly NUM_TICKS ticks
  const finalIntervals = Math.round((niceMax - niceMin) / step);
  const finalStep = (niceMax - niceMin) / finalIntervals;

  // Build tick array
  const ticks: number[] = [];
  for (let i = 0; i <= finalIntervals; i++) {
    // Round to avoid floating-point residue (e.g. 229.99999 → 230)
    ticks.push(Math.round((niceMin + i * finalStep) * 1e6) / 1e6);
  }

  return { domain: [niceMin, niceMax], ticks };
}

/* -------------------------------------------------------------------------- */
/*  PhaseChart                                                                */
/* -------------------------------------------------------------------------- */

export function PhaseChart({ rows, metric, granularity, visiblePhases, height = 260, globalRows }: PhaseChartProps) {
  const { t } = useLanguage();
  const unit = metric === 'voltage' ? 'V' : 'A';
  
  const rawSeries = useMemo<Pt[]>(() => {
    const pts = rows.map((r) =>
      metric === 'voltage'
        ? { t: r.timestamp.getTime(), R: +r.vR.toFixed(1), Y: +r.vY.toFixed(1), B: +r.vB.toFixed(1), AVG: +r.vlnAvg.toFixed(1) }
        : { t: r.timestamp.getTime(), R: +r.currentR.toFixed(1), Y: +r.currentY.toFixed(1), B: +r.currentB.toFixed(1), AVG: +r.currentTotal.toFixed(1) },
    );
    // Clamp each phase to its 99.5th-percentile ceiling so a single bad reading
    // can't distort the chart y-scale or line shape.
    if (pts.length < 10) return pts;
    const fields: (keyof Pt)[] = ['R', 'Y', 'B', 'AVG'];
    const ceilings: Partial<Record<keyof Pt, number>> = {};
    for (const f of fields) {
      const vals = pts.map(p => p[f] as number).filter(v => Number.isFinite(v) && v > 0).sort((a, b) => a - b);
      if (vals.length >= 10) {
        const idx = (99.5 / 100) * (vals.length - 1);
        const lo = Math.floor(idx), hi = Math.ceil(idx);
        ceilings[f] = vals[lo] + (vals[hi] - vals[lo]) * (idx - lo);
      }
    }
    return pts.map(p => {
      const copy = { ...p };
      for (const f of fields) {
        const c = ceilings[f];
        if (c !== undefined && (p[f] as number) > c) (copy as Record<keyof Pt, number>)[f] = c;
      }
      return copy;
    });
  }, [rows, metric]);

  const series = useMemo(() => {
    if (rawSeries.length <= MAX_POINTS) return rawSeries;
    const guide = (['R','Y','B','AVG'] as PhaseKey[]).find((k) => visiblePhases.has(k)) ?? 'R';
    return lttb(rawSeries, MAX_POINTS, guide);
  }, [rawSeries, visiblePhases]);

  const labeled = useMemo(() =>
    series.map((d) => ({ ...d, label: timeLabel(d.t, granularity), tooltipLabel: tooltipLabel(d.t, granularity) })),
  [series, granularity]);

  // ── Y-axis scale (nice, stable across window switches) ───────────────────
  // We collect values from globalRows (all windows) when available so the
  // Y-axis doesn't rescale when the user switches Today / Week / Month.
  // visiblePhases is intentionally excluded from the dependency array for the
  // global pass — hiding a phase should not change the scale.
  const yScale = useMemo(() => {
    // Source rows: prefer globalRows (cross-window) over the current window rows.
    // globalRows contains raw TelemetryRow objects; extract the relevant fields.
    const sourceRows = (globalRows && globalRows.length > 0) ? globalRows : rows;

    const vals: number[] = [];
    for (const r of sourceRows) {
      if (metric === 'voltage') {
        if (isFinite(r.vR)     && r.vR     > 0) vals.push(r.vR);
        if (isFinite(r.vY)     && r.vY     > 0) vals.push(r.vY);
        if (isFinite(r.vB)     && r.vB     > 0) vals.push(r.vB);
        if (isFinite(r.vlnAvg) && r.vlnAvg > 0) vals.push(r.vlnAvg);
      } else {
        if (isFinite(r.currentR)     && r.currentR     >= 0) vals.push(r.currentR);
        if (isFinite(r.currentY)     && r.currentY     >= 0) vals.push(r.currentY);
        if (isFinite(r.currentB)     && r.currentB     >= 0) vals.push(r.currentB);
        if (isFinite(r.currentTotal) && r.currentTotal >= 0) vals.push(r.currentTotal);
      }
    }
    return niceYScale(vals, metric);
  }, [globalRows, rows, metric]);

  const yDomain = yScale.domain;
  const yTicks  = yScale.ticks;

  const animKey = `${granularity}-${metric}-${[...visiblePhases].sort().join('')}`;

  if (!labeled.length) {
    return (
      <div style={{ height }} className="flex items-center justify-center text-sm font-semibold text-slate-700">
        {t("No data for this window")}
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart key={animKey} data={labeled} margin={{ top: 8, right: 8, left: 5, bottom: 0 }}>
          <CartesianGrid stroke="#CBD5E1" vertical={false} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            ticks={symmetricXTicks(labeled, 5)}
            interval={0}
            tick={<XTick />}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={40}
            domain={yDomain}
            ticks={yTicks}
            tick={<YTick />}
          />
          <Tooltip
            content={<PhaseTooltip unit={unit} visiblePhases={visiblePhases} />}
            cursor={{ stroke: '#94A3B8', strokeWidth: 1, strokeDasharray: '4 3' }}
          />
          {metric === 'voltage' && (
            <ReferenceLine
              y={230}
              stroke="#CBD5E1"
              strokeDasharray="6 3"
              label={{
                value: `230V ${t("nom")}`,
                position: 'insideTopRight',
                fontSize: 11,
                fontWeight: 700,
                fill: '#334155',
              }}
            />
          )}
          {(['R','Y','B','AVG'] as PhaseKey[]).map((k) =>
            visiblePhases.has(k) ? (
              <Line
                key={k}
                type="monotoneX"
                dataKey={k}
                stroke={PHASE_META[k].color}
                strokeWidth={k === 'AVG' ? 1.5 : 2}
                strokeDasharray={PHASE_META[k].dash}
                dot={false}
                activeDot={{ r: 3.5, strokeWidth: 0 }}
                isAnimationActive
                animationDuration={380}
                animationEasing="ease-out"
              />
            ) : null
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}